// HeaderComponent.js (renamed from Header.js)
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getProfileData } from "../../../redux/slices/userProfileDataSlice";
import {
  setDeviceConnectionMessage,
  setActiveDeviceCount,
} from "../../../redux/slices/checkDeviceIsConnectionSlice";
import DeviceService from "../../../services/devices/DeviceService";

// MUI Imports
import {
    Avatar,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    Divider,
    ListItemIcon,
    CircularProgress,
    Tooltip
} from "@mui/material";
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import VpnKeyIcon from '@mui/icons-material/VpnKey'; // For Auth Key
import SupportAgentIcon from '@mui/icons-material/SupportAgent'; // For Support

// Removed: import "../../../Styles/Dashboard.css"; // Try to avoid global styles here
// Removed: import { Spinner } from "react-bootstrap";

const HeaderComponent = () => {
  const [userName, setUserName] = useState(null);
  const [profileimg, setProfileImage] = useState(null);
  const dispatch = useDispatch();
  const profileDataState = useSelector((state) => state.profile.profile);
  const [allowMyAppsShow, setAllowMyAppsShow] = useState('no');
  const navigate = useNavigate();

  // For MUI Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // ... (keep your existing useEffects for fetching data: checkDeviceIsConnected, profileData, getUserDeviceNewSubscription)
  // Ensure SentScheduleMessageBySessionId, checkDeviceIsConnected, logActivity, getIPAddress, getUserDeviceNewSubscription
  // are defined or imported if they are still needed in this component's scope.
  // For brevity, I'm assuming they are either here or can be refactored/moved if more appropriate.

  // Example of how one useEffect might look (for profile data)
   useEffect(() => {
    dispatch(getProfileData());
    // ... other initializations from your original useEffect ...
  }, [dispatch]);


  useEffect(() => {
    if (profileDataState && profileDataState.currentUserData.isSuccess && profileDataState.currentUserData.result) {
      const userData = profileDataState.currentUserData.result;
      setUserName(userData);
      if (userData.image) {
        try {
          const binaryString = atob(userData.image);
          const byteArray = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([byteArray], { type: "image/*" });
          setProfileImage(URL.createObjectURL(blob));
        } catch (e) {
          console.error("Error processing profile image:", e);
          setProfileImage(null);
        }
      } else {
        setProfileImage(null);
      }
    }
  }, [profileDataState]);

   useEffect(() => {
    const fetchSubscription = async () => {
        try {
          const url = `${process.env.REACT_APP_NODE_URL}/api/devices/get-total-device-newsubscription`;
          const config = { headers: { token: localStorage.getItem("isToken") } };
          const response = await axios.get(url, config);
          if (response.data && response.data.result && response.data.result.length > 0) {
            const [item] = response.data.result;
            setAllowMyAppsShow(item.is_api_access === 1 || item.is_api_access === '1' || item.is_api_access === 'yes' ? 'yes' : 'no');
          }
        } catch (error) {
          console.error("Error fetching subscription for header:", error);
        }
    };
    fetchSubscription();
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    handleCloseMenu();
  };

  const logActivity = async (activityType = "Logout") => { // Made activityType a param
    try {
      const activityLogData = { activityType };
      const url = `${process.env.REACT_APP_NODE_URL}/api/activitylog/add-activity-log-details`;
      const config = { headers: { token: localStorage.getItem("isToken") } };
      await axios.post(url, activityLogData, config);
    } catch (error) {
      console.error("Error logging activity:", error);
    }
  };

  const handleLogout = async () => {
    // clearInterval(myInterval); // Clear interval if it's managed here
    await logActivity("Logout"); // Log before navigating
    navigate("/Login");
    handleCloseMenu();
  };

  const getInitials = () => {
    if (userName && userName.firstName && userName.lastname) {
      return `${userName.firstName[0]}${userName.lastname[0]}`.toUpperCase();
    } else if (userName && userName.firstName) {
      return `${userName.firstName[0]}`.toUpperCase();
    }
    return "U"; // Default
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
      <Tooltip title="Account settings">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 2 }}
          aria-controls={openMenu ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openMenu ? 'true' : undefined}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }} src={profileimg}>
            {!profileimg && getInitials()}
          </Avatar>
          {userName && (
             <Typography sx={{ minWidth: 100, ml: 1, display: { xs: 'none', sm: 'block' } }} variant="body2">
                {userName.firstName} {userName.lastname}
            </Typography>
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={openMenu}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu} // Closes menu on item click too
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': { // Arrow pointing up
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{fontWeight: 'medium'}}>
                WELCOME!
            </Typography>
            {userName && (
                <Typography variant="caption" color="text.secondary">
                    {userName.email || ''}
                </Typography>
            )}
        </Box>
        <Divider />
        <MenuItem onClick={() => handleNavigate("/profilesetting")}>
          <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
          My Profile
        </MenuItem>
        <MenuItem onClick={() => handleNavigate("/subscription")}>
          <ListItemIcon><SubscriptionsIcon fontSize="small" /></ListItemIcon>
          Subscription
        </MenuItem>
        {allowMyAppsShow === 'yes' && (
          <MenuItem onClick={() => handleNavigate("/myapps")}>
            <ListItemIcon><VpnKeyIcon fontSize="small" /></ListItemIcon>
            Auth Key
          </MenuItem>
        )}
        <MenuItem onClick={() => handleNavigate("/help_support")}>
          <ListItemIcon><SupportAgentIcon fontSize="small" /></ListItemIcon>
          Support
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default HeaderComponent;